import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ImagePlus, Rocket, Sparkles, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { MICROTX_ENABLED } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const MAX_STICKERS = 30;
const MIN_TO_PUBLISH = 3;
const MAX_BYTES = 300 * 1024;
const OK_TYPES = ["image/png", "image/webp", "image/jpeg", "image/gif"];

const errText = (e, fb) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "object") return detail?.message || fb;
  return detail || fb;
};

export default function StickerStudio() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", description: "", price: "0", animated: false });
  const [creating, setCreating] = useState(false);
  const [pack, setPack] = useState(null); // {pack_id, status}
  const [stickers, setStickers] = useState([]); // {sticker_id, emoji, preview}
  const [pending, setPending] = useState(null); // {b64, preview}
  const [emoji, setEmoji] = useState("");
  const [adding, setAdding] = useState(false);
  const [publishing, setPublishing] = useState(false);

  if (!MICROTX_ENABLED) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-bloq-muted" data-testid="studio-unavailable">
        <Sparkles className="h-8 w-8 text-bloq-purple" />
        <p>The Sticker Studio isn't available yet.</p>
        <Button onClick={() => navigate("/app/creator")} variant="outline" className="rounded-full border-white/15 bg-transparent text-white">Back to Creator</Button>
      </div>
    );
  }

  const createPack = async () => {
    if (!form.title.trim()) return toast.error("Give your pack a title.");
    const price = Math.max(0, Math.min(20000, Math.round(Number(form.price || 0) * 100)));
    setCreating(true);
    try {
      const res = await api.createStickerPack({ title: form.title.trim(), description: form.description.trim() || undefined, price, animated: form.animated });
      setPack(res);
      toast.success("Draft pack created — add some stickers!");
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403) toast.error("You need an approved creator account to publish packs.");
      else toast.error(errText(e, "Couldn't create the pack."));
    } finally {
      setCreating(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!OK_TYPES.includes(file.type)) return toast.error("Use a PNG, WEBP, JPEG or GIF.");
    if (file.size > MAX_BYTES) return toast.error("Image is too large (max ~300KB).");
    const fr = new FileReader();
    fr.onload = () => setPending({ b64: String(fr.result).split(",")[1] || "", preview: String(fr.result) });
    fr.readAsDataURL(file);
  };

  const addSticker = async () => {
    if (!pending) return;
    if (stickers.length >= MAX_STICKERS) return toast.error(`Packs are capped at ${MAX_STICKERS} stickers.`);
    setAdding(true);
    try {
      const res = await api.addSticker(pack.pack_id, { image_b64: pending.b64, emoji: emoji.trim() || undefined });
      setStickers((prev) => [...prev, { sticker_id: res.sticker_id, emoji: emoji.trim(), preview: pending.preview }]);
      setPending(null);
      setEmoji("");
    } catch (e) {
      toast.error(errText(e, "Couldn't add that sticker."));
    } finally {
      setAdding(false);
    }
  };

  const publish = async () => {
    if (stickers.length < MIN_TO_PUBLISH) return toast.error(`Add at least ${MIN_TO_PUBLISH} stickers to publish.`);
    setPublishing(true);
    try {
      const res = await api.publishStickerPack(pack.pack_id);
      setPack((p) => ({ ...p, status: res?.status || "published" }));
      toast.success("Pack published to the store! 🎉");
    } catch (e) {
      const code = typeof e?.response?.data?.detail === "object" ? e.response.data.detail.error : null;
      if (code === "payouts_not_setup" || e?.response?.status === 409) toast.error("Set up payouts first to publish a paid pack.");
      else toast.error(errText(e, "Couldn't publish the pack."));
    } finally {
      setPublishing(false);
    }
  };

  const reset = () => { setPack(null); setStickers([]); setPending(null); setEmoji(""); setForm({ title: "", description: "", price: "0", animated: false }); };

  return (
    <div className="h-full overflow-y-auto bg-bloq-bg" data-testid="sticker-studio">
      <div className="mx-auto max-w-2xl px-5 py-6">
        <button onClick={() => navigate("/app/creator")} className="mb-5 flex items-center gap-2 text-sm text-bloq-muted hover:text-white" data-testid="studio-back">
          <ArrowLeft className="h-4 w-4" /> Creator Hub
        </button>
        <h1 className="font-heading text-2xl font-bold text-white">Sticker Studio</h1>
        <p className="mt-1 text-sm text-bloq-muted">Design a pack, upload stickers, and publish it to the store. Paid packs earn you a revenue share.</p>

        {!pack ? (
          <div className="mt-6 space-y-4 rounded-3xl border border-white/8 bg-bloq-s1 p-6" data-testid="studio-create-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Pack title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={60} placeholder="e.g. BLOQ Buddies" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="studio-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Description (optional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={140} placeholder="A short blurb…" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="studio-desc-input" />
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-bloq-muted">Price (USD · 0 = free)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-bloq-muted">$</span>
                  <Input type="number" min="0" max="200" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-11 rounded-xl border-white/12 bg-bloq-bg pl-7" data-testid="studio-price-input" />
                </div>
              </div>
              <button type="button" onClick={() => setForm({ ...form, animated: !form.animated })} className={cn("flex h-11 items-center gap-2 rounded-xl border px-4 text-sm font-semibold", form.animated ? "border-bloq-mint/50 bg-bloq-mint/10 text-bloq-mint" : "border-white/12 text-bloq-muted")} data-testid="studio-animated-toggle">
                {form.animated && <Check className="h-4 w-4" />} Animated
              </button>
            </div>
            <Button onClick={createPack} disabled={creating} className="h-12 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="studio-create-btn">
              {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create draft pack"}
            </Button>
          </div>
        ) : (
          <div className="mt-6 space-y-5" data-testid="studio-editor">
            <div className="flex items-center justify-between rounded-2xl border border-white/8 bg-bloq-s1 px-4 py-3">
              <div className="text-sm font-semibold text-white">{form.title || "Your pack"}</div>
              <span className={cn("rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase", pack.status === "published" ? "bg-bloq-mint/15 text-bloq-mint" : "bg-white/8 text-bloq-muted")} data-testid="studio-status">
                {pack.status || "draft"}
              </span>
            </div>

            {pack.status !== "published" && (
              <div className="rounded-3xl border border-white/8 bg-bloq-s1 p-5">
                <div className="mb-3 flex items-center justify-between text-sm font-semibold text-white">
                  <span>Stickers</span>
                  <span className="text-xs text-bloq-muted" data-testid="studio-count">{stickers.length}/{MAX_STICKERS}</span>
                </div>
                <input ref={fileRef} type="file" accept="image/png,image/webp,image/jpeg,image/gif" onChange={onFile} className="hidden" data-testid="studio-file-input" />
                {pending ? (
                  <div className="flex items-center gap-3">
                    <img src={pending.preview} alt="preview" className="h-16 w-16 rounded-xl border border-white/10 object-contain" />
                    <Input value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4} placeholder="emoji (optional)" className="h-11 w-32 rounded-xl border-white/12 bg-bloq-bg text-center" data-testid="studio-emoji-input" />
                    <Button onClick={addSticker} disabled={adding} className="rounded-full bloq-gradient font-bold text-bloq-bg" data-testid="studio-add-sticker-btn">
                      {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                    </Button>
                    <button onClick={() => setPending(null)} className="text-bloq-muted hover:text-white" data-testid="studio-cancel-pending"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <button onClick={() => fileRef.current?.click()} disabled={stickers.length >= MAX_STICKERS} className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 py-6 text-sm text-bloq-muted hover:border-bloq-mint/40 hover:text-bloq-mint disabled:opacity-40" data-testid="studio-upload-btn">
                    <ImagePlus className="h-5 w-5" /> Upload a sticker image (PNG/WEBP/JPEG/GIF, ≤300KB)
                  </button>
                )}
                {stickers.length > 0 && (
                  <div className="mt-4 grid grid-cols-5 gap-2" data-testid="studio-grid">
                    {stickers.map((s) => (
                      <div key={s.sticker_id} className="relative aspect-square rounded-xl border border-white/8 bg-bloq-s2 p-1.5">
                        <img src={s.preview} alt={s.emoji || "sticker"} className="h-full w-full object-contain" />
                        {s.emoji && <span className="absolute bottom-0 right-0 text-xs">{s.emoji}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {pack.status === "published" ? (
              <div className="rounded-3xl border border-bloq-mint/25 bg-bloq-mint/[0.06] p-6 text-center" data-testid="studio-published">
                <Check className="mx-auto h-8 w-8 text-bloq-mint" />
                <p className="mt-2 font-semibold text-white">Published to the store!</p>
                <div className="mt-4 flex justify-center gap-2">
                  <Button onClick={reset} variant="outline" className="rounded-full border-white/15 bg-transparent text-white" data-testid="studio-new-pack-btn">Create another</Button>
                  <Button onClick={() => navigate("/app/creator")} className="rounded-full bloq-gradient font-bold text-bloq-bg">Done</Button>
                </div>
              </div>
            ) : (
              <Button onClick={publish} disabled={publishing || stickers.length < MIN_TO_PUBLISH} className="h-12 w-full rounded-full bg-bloq-purple font-bold text-white hover:bg-bloq-purple/90 disabled:opacity-50" data-testid="studio-publish-btn">
                {publishing ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Rocket className="mr-1.5 h-4 w-4" /> Publish{stickers.length < MIN_TO_PUBLISH ? ` (need ${MIN_TO_PUBLISH - stickers.length} more)` : ""}</>}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
